import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useState } from "react";

import { SentryLogo } from "@/assets/logos/sentry-logo";
import { useTRPC } from "@/infrastructure/trpc/react";
import { SentryMappingsSkeleton } from "@/sentry/components/sentry-mappings-skeleton";
import { SentryMappingsView } from "@/sentry/components/sentry-mappings-view";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/shared/ui/empty";
import { Label } from "@/shared/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { parseSentryOrganizationUrl } from "@vivus/common";

export const Route = createFileRoute(
  "/app/organization/$id/settings/sentry-mappings"
)({
  loader: async ({ context, params }) => {
    await context.queryClient
      .query(
        context.trpc.sentry.list.queryOptions({
          organizationId: params.id,
        })
      )
      .catch(() => undefined);
  },
  component: RouteComponent,
});

function SentryMappingsContent({
  organizationId,
}: {
  organizationId: string;
}) {
  const trpc = useTRPC();
  const [selectedConnectionId, setSelectedConnectionId] =
    useState<string | null>(null);

  const { data } = useSuspenseQuery(
    trpc.sentry.list.queryOptions({
      organizationId,
    })
  );

  const connections = data?.connections ?? [];

  // Auto-select first connection if none selected
  const effectiveConnectionId =
    selectedConnectionId ?? connections[0]?.id ?? null;

  if (connections.length === 0) {
    return (
      <Empty variant="taller">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <SentryLogo className="size-5" />
          </EmptyMedia>
          <EmptyTitle>No Sentry Connections</EmptyTitle>
          <EmptyDescription>
            Add a Sentry connection in Settings to see
            project mappings.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="space-y-6">
      {/* Connection Selector */}
      {connections.length > 1 && (
        <div className="flex items-center gap-4">
          <Label htmlFor="connection-select">
            Connection:
          </Label>
          <Select
            value={effectiveConnectionId ?? ""}
            onValueChange={setSelectedConnectionId}
          >
            <SelectTrigger
              id="connection-select"
              className="w-64"
            >
              <SelectValue placeholder="Select a connection" />
            </SelectTrigger>
            <SelectContent>
              {connections.map((conn) => {
                const { organizationSlug } =
                  parseSentryOrganizationUrl(
                    conn.sentryOrganizationUrl
                  );
                return (
                  <SelectItem key={conn.id} value={conn.id}>
                    {conn.name} ({organizationSlug})
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Mappings View */}
      {effectiveConnectionId && (
        <Suspense fallback={<SentryMappingsSkeleton />}>
          <SentryMappingsView
            organizationId={organizationId}
            connectionId={effectiveConnectionId}
          />
        </Suspense>
      )}
    </div>
  );
}

function RouteComponent() {
  const { id } = Route.useParams();

  return (
    <Suspense fallback={<SentryMappingsSkeleton />}>
      <SentryMappingsContent organizationId={id} />
    </Suspense>
  );
}
